import { VStack } from '@chakra-ui/react'
import { NodeProps } from '@xyflow/react'
import { useAtomValue } from 'jotai'
import React from 'react'

import {
  externalTransferPhoneNumberDigitLimitsAtom,
  phoneNumbersAtom,
} from '../../atoms'
import { TRANSFER_COMPANY_OPTIONS } from '../../constants/externalTransfer'
import { ExternalTransferNodeData } from '../../converters/shared/reactFlowTypes'
import { useSwitchboardT } from '../../types'
import { text } from '../../utils/text'
import {
  NodeCheckbox,
  NodeInput,
  NodeSearchableCombobox,
  NodeSelect,
} from '../core'
import { BaseNode, nodePropsAreEqual } from './BaseNode'
import { shouldShowExternalTransferSipServer } from './externalTransferSipServerVisibility'
import { useExternalTransferNode } from './hooks/useExternalTransferNode'

const TRANSFER_COMPANY_SEARCH_OPTIONS = TRANSFER_COMPANY_OPTIONS.map(
  ({ value, label }) => ({ id: value, label }),
)

export interface ExternalTransferNodeProps extends NodeProps {
  onDelete?: () => void
  onDeleteNode?: (nodeId: string) => void
  showDeleteButton?: boolean
  // Note: phoneNumbers is provided via Jotai atoms (phoneNumbersAtom)
  // Use useSwitchboardDataAtoms hook in the consuming app to set atoms
}

const ExternalTransferNodeComponent: React.FC<ExternalTransferNodeProps> = ({
  id,
  data,
  onDelete,
  onDeleteNode,
  showDeleteButton,
}) => {
  // Read from Jotai atom
  const phoneNumbers = useAtomValue(phoneNumbersAtom)
  const phoneNumberDigitLimits = useAtomValue(
    externalTransferPhoneNumberDigitLimitsAtom,
  )
  const t = useSwitchboardT()
  const phoneNumberLengthErrorMessage =
    phoneNumberDigitLimits.minDigits === phoneNumberDigitLimits.maxDigits
      ? text(
          t,
          'switchboard.nodes.externalTransfer.phoneNumberExactDigitError',
          'Phone number must be exactly {{count}} digits',
          { count: phoneNumberDigitLimits.minDigits },
        )
      : text(
          t,
          'switchboard.nodes.externalTransfer.phoneNumberDigitRangeError',
          'Phone number must contain {{min}} to {{max}} digits',
          {
            min: phoneNumberDigitLimits.minDigits,
            max: phoneNumberDigitLimits.maxDigits,
          },
        )

  const nodeData = data as ExternalTransferNodeData & Record<string, unknown>
  const {
    phoneNumber,
    timeout,
    sipServer,
    currentPassThroughCallerId,
    currentPhoneNumberId,
    currentTransferCompany,
    currentRequireOneToConnect,
    currentMinimized,
    handlePhoneNumberChange,
    handlePhoneNumberBlur,
    handleTimeoutChange,
    handleTimeoutBlur,
    handlePassThroughCallerIdChange,
    handlePhoneNumberIdChange,
    handleTransferCompanyChange,
    handleRequireOneToConnectChange,
    handleMinimizedChange,
    phoneNumberError,
    hasData,
  } = useExternalTransferNode(
    id,
    nodeData,
    phoneNumberDigitLimits,
    phoneNumberLengthErrorMessage,
  )
  const showSipServer = shouldShowExternalTransferSipServer(sipServer)

  // Format phone numbers for display
  const phoneNumberOptions = React.useMemo(() => {
    return phoneNumbers.map((phone) => ({
      id: phone.id,
      name: phone.name,
      phoneNumber: phone.number,
    }))
  }, [phoneNumbers])
  const renderNumberInput = () => (
    <NodeInput
      label={text(t, 'switchboard.nodes.externalTransfer.number', 'Number')}
      value={phoneNumber}
      onChange={(e) => handlePhoneNumberChange(e.target.value)}
      onBlur={handlePhoneNumberBlur}
      placeholder={text(
        t,
        'switchboard.nodes.externalTransfer.enterPhoneNumber',
        'Enter phone number',
      )}
      required
      error={phoneNumberError || undefined}
    />
  )

  return (
    <BaseNode
      label={text(
        t,
        'switchboard.nodeLabels.external_transfer',
        'EXTERNAL TRANSFER',
      )}
      showPlaceholder={!hasData}
      onDelete={onDelete}
      onDeleteNode={onDeleteNode}
      id={id}
      showDeleteButton={showDeleteButton}
      minimized={currentMinimized}
      onMinimizeToggle={() => handleMinimizedChange(!currentMinimized)}
      minimizedContent={renderNumberInput()}
    >
      <VStack align="stretch" gap={2}>
        {renderNumberInput()}

        <NodeSearchableCombobox
          label={text(
            t,
            'switchboard.nodes.externalTransfer.transferCompany',
            'Transfer Company',
          )}
          options={TRANSFER_COMPANY_SEARCH_OPTIONS}
          value={currentTransferCompany}
          onChange={(value) => handleTransferCompanyChange(value || undefined)}
          placeholder={text(
            t,
            'switchboard.nodes.externalTransfer.searchTransferCompany',
            'Search transfer companies...',
          )}
          searchAriaLabel={text(
            t,
            'switchboard.nodes.externalTransfer.searchTransferCompanyAriaLabel',
            'Search transfer companies',
          )}
          emptyMessage={text(
            t,
            'switchboard.nodes.externalTransfer.noTransferCompanyMatches',
            'No transfer companies match your search.',
          )}
          noneOptionLabel={text(
            t,
            'switchboard.nodes.externalTransfer.noCompany',
            '--',
          )}
          size="sm"
          maxH="240px"
        />

        <NodeInput
          label={text(t, 'switchboard.nodes.transfer.timeout', 'Timeout')}
          type="number"
          value={timeout}
          onChange={(e) => handleTimeoutChange(e.target.value)}
          onBlur={handleTimeoutBlur}
          placeholder="30"
          min={5}
          max={300}
        />

        {showSipServer && (
          <NodeInput
            label={text(
              t,
              'switchboard.nodes.externalTransfer.sipServer',
              'SIP Server',
            )}
            value={sipServer}
            placeholder={text(
              t,
              'switchboard.nodes.externalTransfer.enterSipServer',
              'Enter SIP server',
            )}
            disabled
            readOnly
          />
        )}

        <NodeCheckbox
          label={text(
            t,
            'switchboard.nodes.externalTransfer.passThroughCallerId',
            'Pass Through Caller ID',
          )}
          checked={currentPassThroughCallerId}
          onChange={(e) => handlePassThroughCallerIdChange(e.target.checked)}
        />

        {/* Or Use This Caller ID Dropdown (only shown when pass through is unchecked) */}
        {!currentPassThroughCallerId && (
          <NodeSelect
            label={text(
              t,
              'switchboard.nodes.externalTransfer.orUseThisCallerId',
              'Or Use This Caller ID',
            )}
            value={currentPhoneNumberId}
            onChange={(e) =>
              handlePhoneNumberIdChange(e.target.value || undefined)
            }
          >
            <option value="">
              {text(
                t,
                'switchboard.nodes.externalTransfer.selectCallerId',
                'Select caller ID...',
              )}
            </option>
            {phoneNumberOptions.length === 0 && (
              <option value="" disabled>
                {text(
                  t,
                  'switchboard.nodes.externalTransfer.noPhoneNumbersAvailable',
                  'No phone numbers available',
                )}
              </option>
            )}
            {phoneNumberOptions.map((option) => {
              // Format phone number as (XXX) YYY-ZZZZ
              let formattedPhone = ''
              if (option.phoneNumber) {
                const phone = String(option.phoneNumber).replace(/\D/g, '') // Remove non-digits
                if (phone.length === 10) {
                  formattedPhone = `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`
                } else if (phone.length === 11 && phone[0] === '1') {
                  // Handle 11-digit numbers starting with 1
                  formattedPhone = `(${phone.slice(1, 4)}) ${phone.slice(4, 7)}-${phone.slice(7)}`
                } else {
                  formattedPhone = option.phoneNumber
                }
              }

              // Format: "Name - (XXX) YYY-ZZZZ" (matching legacy format)
              const displayName = option.name
                ? `${option.name}${formattedPhone ? ` - ${formattedPhone}` : ''}`
                : formattedPhone || option.phoneNumber

              // Use the phone number ID as the value (not the phone number string)
              return (
                <option key={option.id} value={option.id}>
                  {displayName}
                </option>
              )
            })}
          </NodeSelect>
        )}

        <NodeCheckbox
          label={text(
            t,
            'switchboard.nodes.externalTransfer.requireOneToConnect',
            'Require 1 to connect',
          )}
          checked={currentRequireOneToConnect}
          onChange={(e) => handleRequireOneToConnectChange(e.target.checked)}
        />
      </VStack>
    </BaseNode>
  )
}

export const ExternalTransferNode = React.memo(
  ExternalTransferNodeComponent,
  nodePropsAreEqual,
)
